import React from 'react';
import { inject, observer } from 'mobx-react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import type { AppStore } from '@stores/app/store';
import type { GoodsAction } from '@stores/goods/action';
import type { BasePageProps, BasePageState } from '../../types/base';
import { BasePage } from '@components/Base/BasePage';
import H5 from './layout/h5';
import styles from './index.module.scss';

interface GoodsDetailRouteParams {
    code?: string;
}

interface GoodsDetailProps extends BasePageProps, RouteComponentProps<GoodsDetailRouteParams> {
    app?: AppStore;
    goods?: GoodsAction;
}

interface GoodsDetailState extends BasePageState {
    resolvingMissingCode: boolean;
}

@inject('app', 'goods')
@observer
class GoodsDetail extends BasePage<GoodsDetailProps, GoodsDetailState> {
    private restoreRootFontSize?: () => void;
    private goodsDetailRequestSequence = 0;
    private goodsListRequestSequence = 0;

    protected getInitialState(): Partial<GoodsDetailState> {
        return {
            ...super.getInitialState(),
            // 缺少 code 时先等待商品列表，避免提前展示商品下架空态。
            resolvingMissingCode: !this.props.match.params.code,
        };
    }

    // 统一处理详情加载和刷新：-4004 交给页面展示下架空态，其他错误返回社区首页。
    private loadGoodsDetail = async () => {
        const { goods, match } = this.props;
        const code = match.params.code;
        const requestSequence = ++this.goodsDetailRequestSequence;

        // 路由未携带商品 code 时只加载推荐列表，不发起无效的详情请求。
        if (!code) {
            goods?.clearGoodsData();
            return null;
        }

        const res = await goods?.getGoodsDetail({ code });

        // 切换商品或离开页面后，忽略旧请求的跳转，避免影响当前页面。
        if (
            requestSequence === this.goodsDetailRequestSequence
            && code === this.props.match.params.code
            && res
            && res.code !== 0
            && Number(res.code) !== -4004
        ) {
            this.props.history.replace('/');
        }

        return res;
    };

    // 加载推荐商品；路由缺少 code 时直接进入列表中的第一件商品。
    private loadGoodsList = async () => {
        const requestedCode = this.props.match.params.code;
        const requestSequence = ++this.goodsListRequestSequence;
        let redirectedToFirstGoods = false;

        if (!requestedCode) {
            this.safeSetState({ resolvingMissingCode: true });
        }

        try {
            const res = await this.props.goods?.getGoodsList({ page: 1, perPage: 10 });

            // 请求期间路由已经变化时，不再处理旧列表结果。
            if (
                requestSequence !== this.goodsListRequestSequence
                || requestedCode !== this.props.match.params.code
            ) return res;

            if (!requestedCode) {
                const firstGoodsCode = this.props.goods?.goodsList?.[0]?.code;
                const hasFirstGoodsCode = firstGoodsCode !== undefined
                    && firstGoodsCode !== null
                    && Boolean(String(firstGoodsCode));

                if (hasFirstGoodsCode) {
                    redirectedToFirstGoods = true;
                    this.props.history.replace(`/goodsDetail/${encodeURIComponent(String(firstGoodsCode))}`);
                }
            }

            return res;
        } finally {
            // 列表为空时结束等待，由页面展示返回社区首页的空状态。
            if (
                !redirectedToFirstGoods
                && requestSequence === this.goodsListRequestSequence
                && requestedCode === this.props.match.params.code
                && !requestedCode
            ) {
                this.safeSetState({ resolvingMissingCode: false });
            }
        }
    };

    private syncH5RemOnDesktop = () => {
        if (typeof window === 'undefined' || this.props.app?.platform !== 'pc') return;

        const root = document.documentElement;
        if (root.style.fontSize === '100px') return;
        const previousFontSize = root.style.fontSize;
        root.style.fontSize = '100px';
        this.restoreRootFontSize = () => {
            // 只恢复本页面设置的值，避免覆盖 resize 过程中写入的新值。
            if (root.style.fontSize === '100px') {
                root.style.fontSize = previousFontSize;
            }
        };
    };

    componentDidMount() {
        document.title = '商品详情';
        this.loadGoodsDetail();
        this.loadGoodsList();
        // H5 样式按 750px 设计稿转换为 rem；PC 下需要保持 1rem = 100px。
        this.syncH5RemOnDesktop();
        super.componentDidMount();
    }

    componentDidUpdate(prevProps: GoodsDetailProps) {
        // 窗口从手机宽度切回 PC 时重新应用 H5 的 rem 基准。
        this.syncH5RemOnDesktop();
        const previousCode = prevProps.match.params.code;
        const currentCode = this.props.match.params.code;

        if (previousCode !== currentCode) {
            this.safeSetState({ resolvingMissingCode: !currentCode });
            this.loadGoodsDetail();
            // 切换商品详情时同步重置推荐列表分页，避免沿用上一个详情页的列表状态。
            this.loadGoodsList();
        }
    }

    componentWillUnmount() {
        // 使未完成的详情请求失效，离开页面后不再执行首页跳转。
        this.goodsDetailRequestSequence += 1;
        this.goodsListRequestSequence += 1;
        this.restoreRootFontSize?.();
        document.title = '';
    }

    render() {
        const { app, goods, match } = this.props;
        const { code } = match.params;
        const isCodeMissing = !code;

        if (!app?.platform) {
            return <div className={styles.loading}>加载中...</div>;
        }

        return (
            <div className={styles.h5Wrap}>
                <H5
                        // 商品 code 变化时重新挂载详情页，清理上一个商品的弹框和滚动状态。
                        key={`goods-detail-h5-${code || 'missing'}`}
                        {...this.props}
                        goodsCode={code || ''}
                        desktopMode={app.platform === 'pc'}
                        goodsData={goods?.goodsData}
                        loading={isCodeMissing ? this.state.resolvingMissingCode : goods?.goodsDetailLoading}
                        error={isCodeMissing ? null : goods?.goodsDetailError}
                        // 缺少 code 且列表为空时，复用 -4004 的返回首页空态。
                        errorCode={isCodeMissing ? -4004 : goods?.goodsDetailErrorCode}
                        goodsList={goods?.goodsList}
                        goodsListTotal={goods?.goodsListTotal}
                        goodsListLoading={goods?.goodsListLoading}
                        getGoodsList={(params: any) => goods?.getGoodsList(params)}
                        onGoodsSaleStart={(goodsCode: string) => goods?.startGoodsSale(goodsCode)}
                        defaultAddress={goods?.defaultAddress}
                        getGoodsAddressList={(params: any) => goods?.getGoodsAddressList(params)}
                        exchangeLoading={goods?.goodsExchangeLoading}
                        exchangeGoods={(params: any) => goods?.exchangeGoods(params)}
                        lotteryLoading={goods?.goodsLotteryLoading}
                        lotteryDraw={(params: any) => goods?.drawLotteryGoods(params)}
                        refreshGoodsData={() => Promise.all([
                            this.loadGoodsDetail(),
                            goods?.getGoodsList(),
                        ])}
                    />
            </div>
        );
    }
}

export default withRouter(GoodsDetail);
